import type {NavigationRouteMatch} from './navigation-context-handler'

/**
 * Identifies the object a page is about, such as a repository, or a preset for pages without one. The field names
 * match the global navigation's context crumbs.
 */
export interface RouteContext {
  /** A context object type (`repository`, `organization`, `team`, `user`, or `business`), or `preset`. */
  crumbType: string
  /** The context object's identifier, such as `owner/name` for a repository, or a preset name like `issues`. */
  crumbObjectIdentifier: string
  /** Path prefix where this context stays valid, such as a repository's `/owner/name`. */
  scope?: string
}

/**
 * Route `handle` keys that declare the page's context, so navigations between routes with different contexts can
 * update page-level UI such as the global navigation. The deepest matched route that sets each key wins.
 */
export interface RouteContextHandle {
  /** The context for the matched params. Routes without one keep the page's context. */
  routeContext?: (params: NavigationRouteMatch['params']) => RouteContext | undefined
  /** The active local navigation tab. Required for contexts with tabs, such as repositories. */
  selectedLink?: string
}

export interface MatchedRouteContext {
  context: RouteContext
  selectedLink?: string
}

export function getRepositoryRouteContext(nameWithOwner: string): RouteContext {
  return {crumbType: 'repository', crumbObjectIdentifier: nameWithOwner, scope: `/${nameWithOwner}`}
}

// Account pages such as `/orgs/acme/projects` share the `/:owner/:repo` shape but aren't repositories.
const ACCOUNT_PATH_PREFIXES = new Set(['orgs', 'users'])

/** For layout routes with `:owner` and `:repo` params, which show that repository's context. */
export const repositoryRouteContextHandle: RouteContextHandle = {
  routeContext: ({owner, repo}) =>
    owner && repo && !ACCOUNT_PATH_PREFIXES.has(owner) ? getRepositoryRouteContext(`${owner}/${repo}`) : undefined,
}

/** The context and tab the matched routes declare, or `undefined` when none declares a context. */
export function getRouteContext(matches: readonly NavigationRouteMatch[]): MatchedRouteContext | undefined {
  let context: RouteContext | undefined
  for (const {handle, params} of matches) {
    const routeContext = (handle as RouteContextHandle | undefined)?.routeContext?.(params)
    if (routeContext) context = routeContext
  }
  return context ? {context, selectedLink: getRouteSelectedLink(matches)} : undefined
}

/** The tab the deepest matched route declares. */
export function getRouteSelectedLink(matches: ReadonlyArray<Pick<NavigationRouteMatch, 'handle'>>): string | undefined {
  let selectedLink: string | undefined
  for (const {handle} of matches) {
    const routeSelectedLink = (handle as RouteContextHandle | undefined)?.selectedLink
    if (routeSelectedLink) selectedLink = routeSelectedLink
  }
  return selectedLink
}

/** A stable key for comparing contexts, or `undefined` without one. */
export function getRouteContextKey(context: RouteContext | undefined): string | undefined {
  return context ? `${context.crumbType}:${context.crumbObjectIdentifier.toLowerCase()}` : undefined
}

export function isSameRouteContext(a: RouteContext, b: RouteContext): boolean {
  return getRouteContextKey(a) === getRouteContextKey(b)
}
